<template>
  <span>
    <v-list-item
      :disabled="!this.enableScaleDataImport || !userAssignedClaim(UserClaims.TicketManager)"
      @click="uploadButtonClicked"
    >
      <v-avatar left>
        <v-icon color="black" :disabled="!this.enableScaleDataImport">mdi-upload</v-icon>
      </v-avatar>
      <v-list-item-title>{{ $t('importScaleData') }}</v-list-item-title>
    </v-list-item>
    <input
      ref="uploader"
      class="d-none"
      type="file"
      @change="fileChanged"
    />
    <Dialog
      :stateId="dialogId"
      :enforcePersistence="confirmImportDataDialogOpen || mapValuesDialog || processingScaleSheet"
      @close="resetDialogs"
      >

      <v-card v-if="processingScaleSheet">
        <v-card-text class="py-6">
          <div class="d-flex align-center justify-center subtitle-1">
            <span>{{ $t('processingRequest') }}</span>
            <v-progress-circular indeterminate color="primary" :size="25" class="ml-4"/>
          </div>
        </v-card-text>
      </v-card>

      <RadioOptionDialog
        v-if="chooseImportProfileDialog"
        :options="scaleSheetProfiles.map(profile => profile.name)"
        :title="$t('chooseImportProfile')"
        :submitButtonText="$t('import')"
        :useI18n="false"
        @close-dialog="resetDialogs"
        @optionChosen="importManager"
      />

      <MapImportProfileValuesDialog
        v-if="mapValuesDialog"
        :key="mapValuesDialogKey"
        :propMappings="unmappedMappings"
        :propTitleKey="activeMappingCategory.titleKey"
        :propLabelKey="activeMappingCategory.labelKey"
        :propAlertKey="activeMappingCategory.alertKey"
        :propWarningKey="activeMappingCategory.warningKey"
        @mappings-selected="handleMappingsSelected"
        @close="resetDialogs"
      />

      <SelectContractForScaleDataTicketDialog
        v-if="reviewContractDataDialog"
        :propContracts="filteredParsedContractData"
        @contracts-selected="handleContractsSelected"
      />

      <ReviewImportDataDialog
        v-if="confirmImportDataDialogOpen"
        :propImportData="parsedScaleData"
        @close="resetDialogs"
        @dataImportSummary="handleImportSummary"
      />

      <ReviewVolumeDataDialog
        v-if="reviewVolumeDataDialog"
        :propVolumeData="parsedScaleData"
        :propSpecies="allSpecies"
        :propGrades="allGrades"
        :propExtTicketNumber2="Number(propTicket.extTicketNumber2)"
        @close-dialog="resetDialogs"
        @reviewed="volumeDataReviewed"
      />

      <ImportErrorsDialog
        v-if="importErrorsDialogOpen"
        :errors="scaleSheetImportErrors"
        :fileName="scaleSheetFileName"
        @close="resetDialogs"
      />

      <ImportSummaryDialog
        v-if="importSummaryDialogOpen"
        :summaryData="importSummaryData"
        :fileName="scaleSheetFileName"
        @close="closeImportSummary"
      />
    </Dialog>
  </span>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { ScalingStatus, ScaleSheetTicketType, ContractType, ScaleDataSource, UserClaims } from '@/utils/Enumerations.js'
import { ScaledTicketCreator } from '@/utils/ScaledTicketCreator'
import { IMPORT_PROFILE_MAPPING_CATEGORIES } from '@/utils/importProfileMappingCategories.js'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { groupBy } from 'lodash'
import { userAssignedClaim } from '@/utils/ClaimUtility'

export default {
  name: 'TicketScaleDataImporter',

  components: {
    ReviewVolumeDataDialog: () => import('@/components/ticket/ticket-details/scale-data/ReviewVolumeDataDialog.vue'),
    ReviewImportDataDialog: () => import('@/components/ticket/ticket-details/scale-data/ReviewImportDataDialog.vue'),
    RadioOptionDialog: () => import('@/components/helper/RadioOptionDialog.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    SelectContractForScaleDataTicketDialog: () => import('@/components/ticket/ticket-details/scale-data/SelectContractForScaleDataTicketDialog.vue'),
    MapImportProfileValuesDialog: () => import('@/components/ticket/ticket-details/scale-data/MapImportProfileValuesDialog.vue'),
    ImportErrorsDialog: () => import('@/components/ticket/ticket-details/scale-data/ImportErrorsDialog.vue'),
    ImportSummaryDialog: () => import('@/components/ticket/ticket-details/scale-data/ImportSummaryDialog.vue')
  },

  props: {
    propTicket: {
      type: Object,
      required: false,
      default: undefined
    },
    mode: {
      type: String,
      required: true,
      validator: value => ['ticket', 'volume'].includes(value)
    }
  },

  created () {
    this.speciesAndGradesFetch = this.fetchSpeciesAndGrades()
  },

  data: () => ({
    dialogId: uniqueDialogId('ticket-scale-data-importer'),
    chooseImportProfileDialog: false,
    invalidLogsDialog: false,
    chosenImportProfile: undefined,
    parsedScaleData: undefined,
    parsedContractData: undefined,
    invalidLogSegments: [],
    isSelecting: false,
    accountTicketBooks: [],
    confirmImportDataDialogOpen: false,
    persistImporting: false,
    reviewVolumeDataDialog: false,
    reviewContractDataDialog: false,
    scaleSheet: undefined,
    importErrorsDialogOpen: false,
    scaleSheetImportErrors: [],
    scaleSheetFileName: '',
    importSummaryDialogOpen: false,
    importSummaryData: null,
    mapValuesDialog: false,
    mapValuesDialogKey: 0,
    processingScaleSheet: false,
    unmappedMappings: [],
    activeMappingCategory: null,
    UserClaims
  }),
  computed: {
    ...mapGetters('scale-sheet', ['scaleSheetProfiles', 'loading']),
    ...mapGetters('species', ['allSpecies']),
    ...mapGetters('grade', ['allGrades']),
    enableScaleDataImport () {
      if (this.mode === 'ticket') return true
      return ((this.propTicket?.extTicketNumber2.trim().length > 0 && this.isLogsContract) || this.isLogYardSaleContract)
    },
    isLogsContract () {
      return this.propTicket?.contractType === ContractType.Production.value || this.propTicket?.contractType === ContractType.WoodsSale.value
    },
    isLogYardSaleContract () {
      return this.propTicket?.contractType === ContractType.LogYardSale.value
    },
    isTicketMode () {
      return this.mode === 'ticket'
    },
    filteredParsedContractData () {
      return this.parsedContractData?.filter(cd => cd.contractId === undefined || cd.productId === undefined) ?? []
    }
  },
  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('scale-sheet', ['fetchProfiles']),
    ...mapActions('scale-data', ['createVolumeData', 'fetchVolumeSummaryForTicket']),
    ...mapMutations('snackbar', ['setSnack']),
    ...mapActions('species', ['fetchSpecies']),
    ...mapActions('grade', ['fetchGrades']),
    userAssignedClaim,

    async fetchSpeciesAndGrades () {
      await this.fetchSpecies()
      await this.fetchGrades()
    },

    // Responses to the parse's own requests clear the global loading overlay mid-parse, so the importer blocks the UI itself.
    async whileProcessingScaleSheet (work) {
      this.processingScaleSheet = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '400' })
      try {
        return await work()
      } finally {
        this.processingScaleSheet = false
      }
    },

    resetDialogs () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.chooseImportProfileDialog = false
      this.reviewVolumeDataDialog = false
      this.reviewContractDataDialog = false
      this.confirmImportDataDialogOpen = false
      this.persistImporting = false
      this.importErrorsDialogOpen = false
      this.scaleSheetImportErrors = []
      this.importSummaryDialogOpen = false
      this.importSummaryData = null
      this.mapValuesDialog = false
      this.processingScaleSheet = false
      this.unmappedMappings = []
      this.activeMappingCategory = null
    },

    async uploadButtonClicked () {
      if (this.propTicket) {
        if (!this.isLogYardSaleContract && !this.propTicket.extTicketNumber2.trim()) {
          this.setSnackError(this.$t('contractRequiresExtTicketNumber2ToUploadScaleData'))
          return
        }
        if (this.propTicket?.scalingStatus === ScalingStatus.Expansion.value) {
          this.setSnackError(this.$t('cannotImportScaleDataForExpandedTicket'))
          return
        }
        if (this.propTicket?.scalingStatus === ScalingStatus.InProgress.value) {
          this.setSnackError(this.$t('cannotImportScaleDataForInProgressTicket'))
          return
        }
        if (this.propTicket?.scalingStatus === ScalingStatus.Recompute.value) {
          this.setSnackError(this.$t('cannotImportScaleDataForRecomputeTicket'))
          return
        }
        if (this.propTicket?.scalingStatus === ScalingStatus.Complete.value) {
          this.volumeSummary = await this.fetchVolumeSummaryForTicket(this.propTicket.ticketId)
          if (this.volumeSummary?.scaleDataSource === ScaleDataSource.Expansion.value ||
          this.volumeSummary?.scaleDataSource === ScaleDataSource.Scaled.value) {
            this.setSnackError(this.$t('canOnlyReImportScaleDataForTicketsThatWerePreviouslyImported'))
            return
          }
        }
      }
      await this.chooseImportProfile()
    },

    async handleContractsSelected (contracts) {
      this.resetDialogs()
      contracts.every(c => {
        this.parsedContractData[c.index].contractId = c.contract.contractId
        this.parsedContractData[c.index].tractId = c.contract.tractId
        this.parsedContractData[c.index].productId = c.product.productId
        this.parsedContractData[c.index].contractType = c.contract.type
        this.parsedContractData[c.index].ticketType = c.ticket[0].hasScaleData && c.product?.isScalable
          ? ScaleSheetTicketType.IncludesVolume
          : c.product?.isScalable && !c.ticket[0].hasScaleData
            ? ScaleSheetTicketType.Expanded
            : ScaleSheetTicketType.WeightBased
        return true
      })
      await this.createTicketData()
      if (this.confirmImportDataDialogOpen) {
        this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
      }
    },

    async chooseImportProfile () {
      await this.fetchProfiles()
      if (this.scaleSheetProfiles.length === 1) {
        this.importManager(this.scaleSheetProfiles[0].name)
        return
      }
      if (this.scaleSheetProfiles.length === 0) {
        this.setSnack(this.$t('noScaleSheetProfiles'))
        return
      }
      this.chooseImportProfileDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '400' })
    },

    importManager (profile) {
      this.resetDialogs()
      this.isSelecting = false
      if (!profile) return
      this.chosenImportProfile = this.scaleSheetProfiles.find(p => p.name === profile)
      this.chooseScaleDataFile()
    },

    chooseScaleDataFile () {
      this.isSelecting = true
      window.addEventListener('focus', () => {
        this.isSelecting = false
      }, { once: true })
      this.$refs.uploader.click()
    },

    async fileChanged (event) {
      const file = event.target.files[0]
      await this.parseScaleData(file)
      this.isSelecting = false
      this.$refs.uploader.value = '' // Reset the file input
      this.chosenImportProfile = undefined
    },

    async parseScaleData (scaleSheetFile) {
      if (!this.propTicket && !this.isTicketMode) return
      this.scaleSheet = new ScaledTicketCreator(this.chosenImportProfile, scaleSheetFile)
      this.scaleSheetFileName = scaleSheetFile?.name ?? ''
      if (this.isTicketMode) {
        this.parsedContractData = undefined
        await this.parseSheetAndPromptMappings()
      } else {
        const contractKeys = {
          contractId: this.propTicket.contractId,
          tractId: this.propTicket.tractId,
          contractAccountId: this.propTicket.accountId,
          fromAccountId: this.propTicket.fromAccountId,
          destinationAccountId: this.propTicket.destinationAccountId,
          settingId: this.propTicket.settingId,
          productId: this.propTicket.productID,
          contractType: this.propTicket.contractType
        }
        this.parsedScaleData = await this.scaleSheet.parseScaleSheetForSingleTicket(this.propTicket.ticketId, this.propTicket.extTicketNumber2, this.propTicket.ticketNumber, contractKeys)
        if (this.parsedScaleData === undefined) {
          this.showImportErrors()
          return
        }
        this.reviewVolumeDataDialog = true
        this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
      }
    },

    async parseSheetAndPromptMappings () {
      const rawTickets = await this.scaleSheet.parseRawTickets()
      // Surface any import errors before prompting for mapping creation.
      if (!rawTickets || this.scaleSheet.importErrors) {
        this.showImportErrors()
        return
      }
      await this.promptNextMappingCategoryOrContinue(0)
    },

    async ensureContractDataParsed () {
      if (this.parsedContractData !== undefined) return true
      this.parsedContractData = await this.whileProcessingScaleSheet(() => this.scaleSheet.parseScaleSheet())
      if (!this.parsedContractData || this.scaleSheet.importErrors) {
        this.showImportErrors()
        return false
      }
      return true
    },

    async findUnmappedMappings (category) {
      if (category.phase === 'scaleData') {
        await this.speciesAndGradesFetch
        return this.scaleSheet.findUnmappedScaleDataCodes(category)
      }
      if (!await this.ensureContractDataParsed()) return undefined
      return this.scaleSheet.findUnmappedProfileMappings(this.parsedContractData, category)
    },

    async promptNextMappingCategoryOrContinue (startCategoryIndex = 0) {
      for (let i = startCategoryIndex; i < IMPORT_PROFILE_MAPPING_CATEGORIES.length; i++) {
        const category = IMPORT_PROFILE_MAPPING_CATEGORIES[i]
        if (category.isApplicable?.(this.scaleSheet.profile) === false) continue
        const unmapped = await this.findUnmappedMappings(category)
        if (unmapped === undefined) return
        if (unmapped.length > 0) {
          this.activeMappingCategory = category
          this.unmappedMappings = unmapped
          this.mapValuesDialogKey++
          this.mapValuesDialog = true
          this.openOrUpdateDialog({ id: this.dialogId, width: '40vw' })
          return
        }
      }
      if (!await this.ensureContractDataParsed()) return
      await this.continueTicketImport()
    },

    // Open the 'Complete Contract Information' dialog for anything still ambiguous, otherwise build the tickets.
    async continueTicketImport () {
      if (this.parsedContractData.some(d => d.contractId === undefined || d.productId === undefined)) {
        this.reviewContractDataDialog = true
      } else {
        await this.createTicketData()
      }
      this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
    },

    // Saving is best-effort: a mapping that fails to persist
    // is still applied locally for the current import.
    async handleMappingsSelected (selections) {
      const category = this.activeMappingCategory
      const nextCategoryIndex = IMPORT_PROFILE_MAPPING_CATEGORIES.indexOf(category) + 1
      const chosen = selections.filter(s => s.value != null)
      this.resetDialogs()
      if (chosen.length === 0) {
        // Nothing was mapped, so the profile (and therefore the parse result) is unchanged.
        await this.promptNextMappingCategoryOrContinue(nextCategoryIndex)
        return
      }
      const mappings = chosen.map(s => ({
        scaleSheetImportProfileId: this.scaleSheet.profile.scaleSheetImportProfileId,
        [category.externalNameField]: s.externalName,
        [category.idField]: s.value
      }))
      const results = await this.whileProcessingScaleSheet(() => Promise.allSettled(mappings.map(m => this.$store.dispatch(category.persistAction, { ...m, childPath: category.childPath }))))
      const created = results.map((r, i) => (r.status === 'fulfilled' && r.value) ? r.value : mappings[i])
      this.scaleSheet.appendCreatedMappings(category, created)
      if (results.every(r => r.status === 'fulfilled')) {
        this.setSnack(this.$t('importMappingsSaved'))
      } else {
        this.setSnackError(this.$t('importMappingsSaveFailed'))
      }
      this.parsedContractData = undefined
      await this.promptNextMappingCategoryOrContinue(nextCategoryIndex)
    },

    showImportErrors () {
      if (this.scaleSheet.importErrors) {
        this.scaleSheetImportErrors = this.scaleSheet.importErrors.errors
        this.importErrorsDialogOpen = true
        this.openOrUpdateDialog({ id: this.dialogId, width: '600px' })
      } else {
        this.closeDialogsAtOrAbove(this.dialogId)
      }
    },

    async createTicketData () {
      const tickets = Object.entries(groupBy(this.parsedContractData.flatMap(cd => {
        return cd.ticket.map(t => {
          return {
            contractId: cd.contractId,
            tractId: cd.tractId,
            productId: cd.productId,
            ticketType: cd.ticketType,
            contractType: cd.contractType,
            ...t
          }
        })
      }), t => JSON.stringify([t.ticketNumber, t.contractId]))).map(([key, value]) => {
        const [ticketNumber] = JSON.parse(key)
        return {
          ticketNumber,
          ticketContent: value
        }
      })
      this.parsedScaleData = await this.whileProcessingScaleSheet(() => this.scaleSheet.constructTicketsFromScaleSheet(tickets))
      if (this.parsedScaleData === undefined) {
        this.showImportErrors()
        return
      }
      this.confirmImportDataDialogOpen = true
    },

    async volumeDataReviewed () {
      this.resetDialogs()
      await this.uploadScaleData()
    },

    async uploadScaleData () {
      if (this.parsedScaleData !== undefined && this.parsedScaleData.segmentVolumes.length > 0) {
        const forceReScale = this.propTicket?.scalingStatus === ScalingStatus.Complete.value
        await this.createVolumeData({ volumeData: this.parsedScaleData, forceReScale })
        this.$emit('refresh')
      }
      this.parsedScaleData = undefined
      this.chosenImportProfile = undefined
    },

    async readFileAsString (file) {
      return await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.addEventListener('error', reject)
        reader.addEventListener('load', () => { resolve(reader.result) })
        reader.readAsText(file)
      })
    },

    handleImportSummary (summaryData) {
      this.persistImporting = false
      this.resetDialogs()
      this.importSummaryData = summaryData
      this.importSummaryDialogOpen = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
    },

    closeImportSummary () {
      this.resetDialogs()
      this.$emit('refresh')
    }
  }
}
</script>
